import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Heart, Play, Pause, ArrowLeft, Loader2, Gift } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { ShareButton } from "@/components/ShareButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DedicationView() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { playIndex, current, isPlaying, togglePlay } = usePlayer();
  const [ded, setDed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => { axios.get(`${API}/dedications/${id}`).then((r) => setDed(r.data)).catch(() => setDed(false)).finally(() => setLoading(false)); }, [id]);

  if (loading) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg)", color: "var(--text-muted)" }}><Loader2 className="animate-spin" /></div>;
  if (!ded) return <div className="min-h-screen grid place-items-center font-ui" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>Not found. <Link to="/rooms/sindu-kamare/dedications" className="underline ml-2">Back</Link></div>;

  const song = ded.song;
  const active = song && current && current.id === song.id;
  const onPlay = () => { if (active) togglePlay(); else if (song) playIndex(0, [song]); };

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />
      {!revealed && (
        <div className="fixed inset-0 z-50 grid place-items-center px-6 cursor-pointer" style={{ background: "linear-gradient(160deg, var(--player-bg) 0%, #3A2412 120%)" }} data-testid="dedication-envelope" onClick={() => setRevealed(true)}>
          <div className="text-center sk-scale-in">
            <div className="text-7xl sk-glow inline-block rounded-full" style={{ padding: 8 }}>💌</div>
            <p className="font-si text-xl mt-6" style={{ color: "rgba(255,255,255,0.85)" }}>{t("dd_for")} <span className="font-bold">{ded.to_name}</span></p>
            <button data-testid="dedication-reveal-button" className="mt-6 h-14 px-8 rounded-full font-ui text-lg font-bold inline-flex items-center gap-2 transition-transform active:scale-95" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>{t("dd_reveal")}</button>
          </div>
        </div>
      )}
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl p-8 text-center sk-scale-in" style={{ background: "linear-gradient(160deg, var(--player-bg) 0%, #3A2412 120%)", color: "#FFFDF7" }} data-testid="dedication-view">
          <span className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full" style={{ background: "rgba(245,158,11,0.18)", color: "var(--player-accent)" }}><Gift size={14} /> {t("dd_nav")}</span>
          <p className={`${lang === "si" ? "font-si" : "font-en"} text-lg mt-5`} style={{ color: "rgba(255,255,255,0.75)" }}>{t("dd_for")}</p>
          <h1 className={`${lang === "si" ? "font-si" : "font-en"} text-3xl sm:text-4xl font-bold`} style={{ color: "#FFFDF7" }}>{ded.to_name}</h1>

          {song && <img src={song.cover_url} alt="" className="w-52 h-52 rounded-3xl object-cover mx-auto mt-6 shadow-2xl" style={{ border: "1px solid rgba(255,255,255,0.12)" }} />}

          {ded.message && <p className={`${lang === "si" ? "font-si" : "font-en"} text-xl mt-6 leading-relaxed`} style={{ color: "rgba(255,255,255,0.9)" }}>“{ded.message}”</p>}

          {song && (
            <>
              <p className={`${lang === "si" ? "font-si" : "font-en"} text-xl font-bold mt-6`} style={{ color: "#FFFDF7" }}>{lang === "si" ? song.title_si : song.title_en}</p>
              <p className="font-ui text-base" style={{ color: "rgba(255,255,255,0.65)" }}>{song.artist}</p>
              <button onClick={onPlay} data-testid="dedication-play-button" className="mt-6 inline-flex items-center gap-2 h-14 px-8 rounded-full font-ui text-lg font-bold transition-transform active:scale-95 sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>
                {active && isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />} {t("dd_play")}
              </button>
            </>
          )}

          <p className="font-ui text-sm mt-8" style={{ color: "rgba(255,255,255,0.6)" }}>— {ded.from_name} <Heart size={13} className="inline" fill="currentColor" style={{ color: "var(--player-accent)" }} /></p>

          <div className="mt-7 flex justify-center">
            <ShareButton testid="dedication-share" dark text={`${ded.from_name} sent ${ded.to_name} a song dedication 🎁🎵`} />
          </div>
        </div>

        <Link to="/rooms/sindu-kamare/dedications" className="flex items-center justify-center gap-2 font-ui text-sm font-semibold mt-6" style={{ color: "var(--accent)" }}><ArrowLeft size={16} /> {t("dd_nav")}</Link>
      </div>
      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
